import React, { useEffect, useState } from 'react'
import getFullAddressString from '@ancon/wildcat-utils/user/getFullAddressString'
import { OrderFormat, ServiceTimeKindType } from '@ancon/wildcat-types'
import noop from 'lodash/noop'

import Page from '../../../features/layout/components/Page'
import { fetchOutletListItem } from '../../../features/outlet/store/outletThunks'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  outletDetailsSelector,
  outletFiltersSelector,
  outletIsVisibleServiceTimeKindSelectionModalSelector,
  outletListItemSelector,
} from '../../../features/outlet/store/outletSelector'
import getAppServerSideProps from '../../../store/ssr/getAppServerSideProps'
import OutletDetailsHeader from '../../../features/outlet/components/OutletDetailsHeader'
import OrderConfigurationModal from '../../../features/outlet/components/orderConfigurationModal/OrderConfigurationModal'
import NavbarOutletSearch from '../../../features/layout/components/main/navBar/NavbarOutletSearch'
import NavbarOrderFormatFilter from '../../../features/layout/components/main/navBar/NavbarOrderFormatFilter'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import OutletMenu from '../../../features/outlet/components/outletMenu/OutletMenu'
import OutletChangeWarningModal from '../../../features/outlet/components/OutletChangeWarningModal'
import OutletDetailsModal from '../../../features/outlet/components/outletDetailsModal/OutletDetailsModal'
import ClientAuthContainer from '../../../features/auth/components/ClientAuthContainer'
import TableOrderingFilter from '../../../features/outlet/components/TableOrderingFilter'
import OutletMenuSkeleton from '../../../features/outlet/components/outletMenu/OutletMenuSkeleton'
import {
  outletForceRefreshMenuIfNeeded,
  outletSetIsVisibleDeliveryLocationModal,
  outletSetIsVisibleServiceTimeKindSelectionModal,
  outletSetOutletFilters,
} from '../../../features/outlet/store/outletSlice'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  checkoutFiltersSelector,
  checkoutIsSystemProductSelector,
  outletSelectedOutletHasCheckoutSelector,
} from '../../../features/checkout/store/checkoutSelectors'
import NavBarOrderTimeSwitch from '../../../features/layout/components/main/navBar/NavBarOrderTimeSwitch'
import NewOrderTimeConfigureModal from '../../../features/outlets/components/newOrderTimeConfigureModal/NewOrderTimeConfigureModal'
import isAppWhiteLabeled from '../../../features/app/utils/isAppWhiteLabeled'
import { clientContextIsCompanyUserSelector } from '../../../features/clientContext/store/clientContextSelectors'
import OutletDetailsURLFilter from '../../../features/outlet/components/OutletDetailsURLFilter'
import { fetchGiftCardAddon } from '../../../features/giftCards/store/giftCardsThunks'
import { OutletGiftCardPurchaseURLFlag } from '../../../features/outlet/constants'
import { OutletPageParams } from '../../../features/outlet/types'
import CheckoutSystemProductWarningModal from '../../../features/checkout/components/systemProductsWarning/CheckoutSystemProductWarningModal'
import getServerSideGiftCardPurchaseRedirect from '../../../features/giftCards/utils/getServerSideGiftCardPurchaseRedirect'
import getOutletLogoImageUrl from '../../../features/outlet/utils/getOutletLogoImageUrl'

import styles from './index.module.scss'

function OutletDetailsPage() {
  const dispatch = useAppDispatch()

  const store = useAppStore()

  const outlet = useAppSelector(outletListItemSelector)
  const outletFilter = useAppSelector(outletFiltersSelector)
  const outletDetails = useAppSelector(outletDetailsSelector)
  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)

  const isWhiteLabeled = isAppWhiteLabeled()

  const isCompanyDashboard = !!isCompanyUser && isWhiteLabeled

  const [shouldDeliveryLocationModalOpen, setShouldDeliveryLocationModalOpen] =
    useState(true)
  const outletHasCheckout = useAppSelector(
    outletSelectedOutletHasCheckoutSelector,
  )
  const isSystemProductCheckout = useAppSelector(
    checkoutIsSystemProductSelector,
  )
  const isVisibleServiceTimeKindSelectionModal = useAppSelector(
    outletIsVisibleServiceTimeKindSelectionModalSelector,
  )

  const { name, address } = outlet || {}

  // Open delivery location modal for delivery order format
  useEffect(() => {
    if (
      shouldDeliveryLocationModalOpen &&
      outlet &&
      !outletFilter.delivery &&
      outletFilter.orderFormat === OrderFormat.Delivery
    ) {
      dispatch(outletSetIsVisibleDeliveryLocationModal(true))
      setShouldDeliveryLocationModalOpen(false)
    }

    if (
      outletFilter.orderFormat !== OrderFormat.Delivery &&
      shouldDeliveryLocationModalOpen
    ) {
      setShouldDeliveryLocationModalOpen(false)
    }
  }, [
    shouldDeliveryLocationModalOpen,
    dispatch,
    outlet,
    outletDetails?.id,
    outletFilter.delivery,
    outletFilter.orderFormat,
  ])

  useEffect(() => {
    if (outletHasCheckout && !isSystemProductCheckout) {
      const checkoutFilters = checkoutFiltersSelector(store.getState())
      // Keep the frozen serviceTime for scheduled orders, but let ASAP orders resolve to "now"
      const isAsSoonAsPossible =
        checkoutFilters.serviceTime?.kind ===
        ServiceTimeKindType.AsSoonAsPossible
      const { serviceTime, ...restoredFilters } = checkoutFilters

      dispatch(
        outletSetOutletFilters(
          isAsSoonAsPossible ? restoredFilters : checkoutFilters,
        ),
      )
      dispatch(outletForceRefreshMenuIfNeeded())
    }
  }, [dispatch, isSystemProductCheckout, outletHasCheckout, store])

  function handleOnCloseServiceTimeKindSelectionModal() {
    dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(false))
  }

  return (
    <Page
      description={getFullAddressString(address)}
      title={name}
      imageSrc={getOutletLogoImageUrl(outlet)}
    >
      {outlet && (
        <>
          <div className={styles.actionButtons}>
            {!isCompanyDashboard && <NavbarOutletSearch isInNavBar={false} />}
            <div className={styles.actionButtonsGroup}>
              <NavbarOrderFormatFilter isInNavBar={false} />
              <NavBarOrderTimeSwitch isInNavBar={false} />
            </div>
          </div>
          <OutletDetailsHeader />
          <ClientAuthContainer skeleton={<OutletMenuSkeleton />}>
            <OutletMenu />
            <OrderConfigurationModal />
            <OutletChangeWarningModal />
            <CheckoutSystemProductWarningModal />
            <TableOrderingFilter />
            <OutletDetailsURLFilter />
            <OutletDetailsModal />
            {outletFilter?.orderFormat && (
              <NewOrderTimeConfigureModal
                selectedOrderFormat={outletFilter?.orderFormat}
                isOpen={isVisibleServiceTimeKindSelectionModal}
                onClose={handleOnCloseServiceTimeKindSelectionModal}
              />
            )}
          </ClientAuthContainer>
        </>
      )}
    </Page>
  )
}

export const getServerSideProps = getAppServerSideProps(
  async ({ store, nextCtx }) => {
    const { query } = nextCtx

    const outletSlug = (query as OutletPageParams)?.outletSlug

    const response = await store
      .dispatch(
        fetchOutletListItem({ urlSlug: outletSlug, isServerSide: true }),
      )
      .unwrap()

    if (outletSlug) {
      try {
        await store
          .dispatch(fetchGiftCardAddon({ outletSlug, isServerSide: true }))
          .unwrap()

        const hasGiftCardPurchaseFlag = Object.keys(query).some(
          param => param.toLowerCase() === OutletGiftCardPurchaseURLFlag,
        )

        // For white-label apps, redirect to gift card purchase page if the flag is present
        if (isAppWhiteLabeled() && hasGiftCardPurchaseFlag) {
          return {
            redirect: getServerSideGiftCardPurchaseRedirect(nextCtx),
          }
        }
      } catch (error) {
        noop()
      }
    }

    // if outlet not found, redirect to landing page
    if (!response) {
      return {
        redirect: {
          destination: '/',
          permanent: false,
        },
      }
    }
    return {
      props: {} as never,
    }
  },
)

export default OutletDetailsPage
